import { useMemo } from 'react'

import { Skeleton } from '@mantine/core'

import type { HeatmapCell, HeatmapSummary } from './constants'
import { CoverageBar } from './CoverageBar'
import { CoverageStats, type BusiestCell } from './CoverageStats'
import { HeatmapGrid } from './HeatmapGrid'
import { HeatmapLegend } from './HeatmapLegend'

const cellCalls = (cell: HeatmapCell): number => cell.calls

export interface CallCoverageHeatmapViewProps {
  timezone: string
  cells: HeatmapCell[]
  summary: HeatmapSummary | null
  loading?: boolean
}

export const CallCoverageHeatmapView = ({
  timezone,
  cells,
  summary,
  loading = false,
}: CallCoverageHeatmapViewProps) => {
  const busiest = useMemo<BusiestCell | null>(() => {
    let best: BusiestCell | null = null
    for (const cell of cells) {
      if (cell.calls > 0 && (best === null || cell.calls > best.value)) {
        best = { dayOfWeek: cell.dayOfWeek, hour: cell.hour, value: cell.calls }
      }
    }
    return best
  }, [cells])

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-2.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={72} radius="md" />
          ))}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} height={28} radius="sm" />
        ))}
        <Skeleton height={180} radius="md" />
      </div>
    )
  }

  if (cells.length === 0 || summary === null) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        No calls in this period.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      <CoverageStats summary={summary} busiest={busiest} />

      {/* One staffed/off-hours row per actor — comparing the Employees and
          Voice agent rows is what tells the coverage story, no toggle. */}
      <div className="flex flex-col gap-2">
        <CoverageBar
          label="All"
          staffed={summary.totalCalls - summary.offHoursCalls}
          offHours={summary.offHoursCalls}
        />
        <CoverageBar
          label="Employees"
          staffed={summary.humanCalls - summary.offHoursHumanCalls}
          offHours={summary.offHoursHumanCalls}
        />
        <CoverageBar
          label="Voice agent"
          staffed={summary.voiceAgentCalls - summary.offHoursVoiceAgentCalls}
          offHours={summary.offHoursVoiceAgentCalls}
        />
      </div>

      <div>
        <div className="mb-1.5 text-xs font-medium text-doubleNickel-gray-800">
          Volume by Local Hour
        </div>

        <HeatmapGrid cells={cells} getValue={cellCalls} />

        <div className="mt-2 flex flex-col gap-1.5">
          <div className="text-[11px] text-doubleNickel-gray-500">
            Staffed hours: Mon–Fri 9am–5pm ({timezone})
          </div>
          <HeatmapLegend />
        </div>
      </div>
    </div>
  )
}

export default CallCoverageHeatmapView
